// -----------------------------------------------------------------------
// Capability icon-set - custom line-art SVGs, boutique signature.
// Replaces generic Unsplash stock photos on the Capabilities page so the
// three pillars read as a crafted, Dataleo-specific illustration system.
//
// Design rules:
//  - 1.6px strokeWidth, round linecap, round linejoin
//  - viewBox 0 0 160 160 - square frame
//  - Current color drives the stroke so we can theme light/dark via text-*
//  - Minimal fill so the icons feel editorial, not filled graphics
// -----------------------------------------------------------------------

const base = {
  width: "100%",
  height: "100%",
  viewBox: "0 0 160 160",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  xmlns: "http://www.w3.org/2000/svg",
};

// 01 · Advisory & Transformation - a target with a plan compass. Rings
// convey multi-horizon framing (strategy → design → implementation).
export const AdvisoryIcon = (props) => (
  <svg {...base} aria-hidden="true" {...props}>
    {/* Outer ring with notches (compass) */}
    <circle cx="80" cy="80" r="56" />
    <circle cx="80" cy="80" r="40" strokeDasharray="2 6" />
    <circle cx="80" cy="80" r="22" />
    <circle cx="80" cy="80" r="4" fill="currentColor" stroke="none" />
    {/* Compass arrows pointing to 4 cardinal directions */}
    <path d="M80 10 L80 24 M80 136 L80 150 M10 80 L24 80 M136 80 L150 80" />
    {/* Diagonal accent - strategic trajectory */}
    <path d="M50 50 L110 110" strokeDasharray="3 5" />
    <path d="M110 110 L110 95 M110 110 L95 110" />
    {/* Small flag on upper-right - the "where to" */}
    <path d="M118 42 L118 24 L132 32 L118 36" fill="currentColor" stroke="currentColor" />
  </svg>
);

// 02 · Technology & Delivery - an APS console: window frame with chart
// bars + sparkline + control knob. Clearly a planning dashboard.
export const DeliveryIcon = (props) => (
  <svg {...base} aria-hidden="true" {...props}>
    {/* Monitor / window frame */}
    <rect x="18" y="22" width="124" height="98" rx="4" />
    {/* Title bar */}
    <path d="M18 36 L142 36" />
    <circle cx="26" cy="29" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="32" cy="29" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="38" cy="29" r="1.6" fill="currentColor" stroke="none" />
    {/* Bar chart */}
    <path d="M30 100 L30 76" strokeWidth="4" />
    <path d="M44 100 L44 60" strokeWidth="4" />
    <path d="M58 100 L58 70" strokeWidth="4" />
    <path d="M72 100 L72 54" strokeWidth="4" />
    {/* Baseline */}
    <path d="M28 104 L130 104" />
    {/* Sparkline trending up */}
    <path d="M88 90 L100 78 L112 84 L124 64 L132 70" />
    <circle cx="132" cy="70" r="2.5" fill="currentColor" stroke="none" />
    {/* Stand base */}
    <path d="M60 120 L60 134 L100 134 L100 120" />
    <path d="M46 134 L114 134" />
  </svg>
);

// 03 · AI & Decision Build - a brain-circuit hybrid: nodes & arcs
// forming a decision graph that converges to one output arrow.
export const AiBuildIcon = (props) => (
  <svg {...base} aria-hidden="true" {...props}>
    {/* Central node */}
    <circle cx="80" cy="80" r="12" />
    <circle cx="80" cy="80" r="3" fill="currentColor" stroke="none" />
    {/* Input nodes left */}
    <circle cx="22" cy="38" r="6" />
    <circle cx="22" cy="80" r="6" />
    <circle cx="22" cy="122" r="6" />
    {/* Hidden layer */}
    <circle cx="60" cy="50" r="4" />
    <circle cx="60" cy="110" r="4" />
    {/* Output nodes right */}
    <circle cx="118" cy="60" r="6" />
    <circle cx="118" cy="100" r="6" />
    {/* Final decision node */}
    <circle cx="146" cy="80" r="4" fill="currentColor" />
    {/* Edges - input → hidden */}
    <path d="M28 38 L56 50 M28 80 L56 50 M28 80 L56 110 M28 122 L56 110" />
    {/* Edges - hidden → central */}
    <path d="M64 50 L72 72 M64 110 L72 88" />
    {/* Edges - central → outputs */}
    <path d="M88 76 L112 62 M88 84 L112 100" />
    {/* Edges - outputs → final */}
    <path d="M124 60 L142 78 M124 100 L142 82" />
    {/* Decision arrow */}
    <path d="M150 80 L156 80 M150 80 L146 76 M150 80 L146 84" strokeWidth="2" />
    {/* Accent - thought spark top */}
    <path d="M80 32 L80 28 M76 36 L72 32 M84 36 L88 32" strokeWidth="2" />
  </svg>
);

// Lookup table consumed by Capabilities to pick the right icon per slug.
export const CAPABILITY_ICONS = {
  advisory: AdvisoryIcon,
  delivery: DeliveryIcon,
  "ai-build": AiBuildIcon,
};
